import {
    type LatestProcessCollectStandardizedOutput,
    type NodeCfg,
    type NodeControlSocketKindMap,
    type NodeDataSocketKindMap,
    type NodeSocketKind,
    type NodeSocketKindMap,
} from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { UiNodeSpec } from './UiNodeSpec';
import { gqlClient } from 'src/util/gql-client';
import {
    GetProcessByIdForProcessCollectNodeDocument,
    type ProcessDataElement,
    ProcessDataElementCollectionKindEnum,
    type ProcessDataElementKind,
    ProcessDataElementKindEnum,
    SelectProcessByIdDocument,
} from 'types/db';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import { removeNodeSocket } from '../../sync-utils/remove-node-socket';
import type { UiFlowNodeInput } from '../../sockets/UiFlowNodeInput';
import type { UiFlowNodeOutput } from '../../sockets/UiFlowNodeOutput';

const getDataElementsForProcessCollectNode = (payload: (Pick<ProcessDataElement, 'id' | 'name' | 'collection_kind'> & {
    data_element_kind: Pick<ProcessDataElementKind, 'kind'>
})[]): NodeCfg<'processCollect'>['controlValues']['dataElements'] => {
    const dataElements: NodeCfg<'processCollect'>['controlValues']['dataElements'] = payload
        .filter(de => de.collection_kind === ProcessDataElementCollectionKindEnum.Collect || de.collection_kind === ProcessDataElementCollectionKindEnum.SpecParam)
        .map<NodeCfg<'processCollect'>['controlValues']['dataElements'][number]>(de => {
        const type: NodeCfg<'processCollect'>['controlValues']['dataElements'][number]['type'] = (() => {
            if (de.data_element_kind.kind === 'string') {
                return ProcessDataElementKindEnum.String;
            } else if (de.data_element_kind.kind === 'number') {
                return ProcessDataElementKindEnum.Number;
            } else if (de.data_element_kind.kind === 'pass') {
                return ProcessDataElementKindEnum.Pass;
            } else if (de.data_element_kind.kind === 'material') {
                return ProcessDataElementKindEnum.Material;
            } else if (de.data_element_kind.kind === 'material_model') {
                return ProcessDataElementKindEnum.MaterialModel;
            } else if (de.data_element_kind.kind === 'material_class') {
                return ProcessDataElementKindEnum.MaterialClass;
            }
            return ProcessDataElementKindEnum.String;
        })();
        return {
            type,
            id: de.id,
            name: de.name,
            sourceVarId: '',
            collectionKind: de.collection_kind as ProcessDataElementCollectionKindEnum.Collect | ProcessDataElementCollectionKindEnum.SpecParam,
        };
    });
    return dataElements;
};

const deletableOutputHelper = <S extends NodeSocketKind>(payload: {
    label: string;
    value: keyof NodeSocketKindMap['processCollect']['outputs'];
    nodeCfg: NodeCfg<'processCollect'>;
    outputType: S;
}): UiFlowNodeInput.Opts<S, string> => {
    return {
        socket: payload.outputType,
        control: {
            cfg: {
                rightAlign: true,
                label: payload.label,
                kind: 'deletableSocket',
                onDelete () {
                    removeNodeSocket<'processCollect'>({
                        direction: 'output',
                        removalMethod: {
                            kind: 'uniqueIdentifier',
                            value: payload.value,
                        },
                        newNodeCfg: {
                            ...payload.nodeCfg,
                            controlValues: {
                                ...payload.nodeCfg.controlValues,
                                useStandardizedOutputs: {
                                    ...payload.nodeCfg.controlValues.useStandardizedOutputs,
                                    [payload.value]: false,
                                },
                            },
                        },
                    });
                },
            },
            nodeCfg: payload.nodeCfg,
            value: '',
        },
    };
};

const processCollectColumnLabels = {
    status: 'Status',
    specId: 'Spec ID',
    badCount: 'Bad Count',
    specName: 'Specification Name',
    goodCount: 'Good Count',
    requiredCount: 'Required Count',
    validationError: 'Validation Error',
} satisfies {
    [key in keyof LatestProcessCollectStandardizedOutput]: string;
};

const processCollectColumnSelectionMap = Object.entries(processCollectColumnLabels).map(([ key, value ]) => ({
    label: value,
    value: key,
}));

export const processCollectUiNodeSpec: UiNodeSpec<'processCollect', NodeCfg<'processCollect'>, NodeSocketKindMap['processCollect']> = {
    nodeCategory: 'argonautApi',
    requiredSockets: [ 'materialId' ],
    kindLabel: async cfg => {
        if (cfg.kind !== 'processCollect') {
            return 'Process Collect';
        }
        const typedNode = cfg as NodeCfg<'processCollect'>;
        if (typedNode.controlValues.processId) {
            const result = await gqlClient.request({
                document: SelectProcessByIdDocument,
                variables: {
                    processId: typedNode.controlValues.processId,
                },
            });
            if (result.process_process_by_pk?.name) {
                return result.process_process_by_pk.name;
            }
        }
        return 'Process Collect';
    },
    controls: getNodeCfg => {
        const ret: ReturnType<UiNodeSpec<'processCollect'>['controls']> = {
            processId: ({
                nodeCfg: getNodeCfg(),
                value: getNodeCfg().controlValues.processId,
                cfg: {
                    kind: 'selectProcess',
                    getValue: n => n.kind === 'processCollect' ? n.controlValues.processId : null,
                    onSelect: async processId => {
                        const {
                            process_process_by_pk,
                        } = await gqlClient.request({
                            document: GetProcessByIdForProcessCollectNodeDocument,
                            variables: {
                                processId,
                            },
                        });
                        if (!process_process_by_pk) {
                            throw new Error(`Process with ID ${processId} not found.`);
                        }
                        const dataElements = getDataElementsForProcessCollectNode(process_process_by_pk.data_elements);
                        const nodeCfg = getNodeCfg();
                        createNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    processId,
                                    dataElements,
                                },
                            },
                        });
                    },
                },
            }),
            location: ({
                nodeCfg: getNodeCfg(),
                value: getNodeCfg().controlValues.location,
                cfg: {
                    kind: 'location',
                },
            }),
            useStandardizedOutputs: ({
                    nodeCfg: getNodeCfg(),
                    value: getNodeCfg().controlValues.useStandardizedOutputs,
                    cfg: {
                        selectLabel: 'Add Output',
                        kind: 'selectWithCallback',
                        items: () => {
                            const nodeCfg = getNodeCfg();
                            const notUsedOutputs = Object.entries(nodeCfg.controlValues.useStandardizedOutputs).filter(([ , value ]) => !value).map(([ key ]) => key);
                            return processCollectColumnSelectionMap.filter(item => notUsedOutputs.includes(item.value));
                        },
                        onSelect (value) {
                            const nodeCfg = getNodeCfg();
                            const typedValue = value as keyof typeof nodeCfg.controlValues.useStandardizedOutputs;
                            createNodeSocket({
                                newNodeCfg: {
                                    ...nodeCfg,
                                    controlValues: {
                                        ...nodeCfg.controlValues,
                                        useStandardizedOutputs: {
                                            ...nodeCfg.controlValues.useStandardizedOutputs,
                                            [typedValue]: true,
                                        },
                                    },
                                },
                            });
                        },
                    },
                }),
        };
        return ret;
    },
    cfg: () => {
        const processCollectNodeCfg: NodeCfg<'processCollect'> = {
            disabled: false,
            id: getUID(),
            kind: 'processCollect',
            controlValues: {
                processId: '',
                dataElements: [],
                location: {
                    id: '',
                },
                useStandardizedOutputs: {
                    status: true,
                    specId: true,
                    badCount: true,
                    specName: true,
                    goodCount: true,
                    requiredCount: true,
                    validationError: true,
                },
            },
        };
        return processCollectNodeCfg;
    },
    inputs: nodeCfg => {
        const controlInputs: {
            [k in keyof NodeControlSocketKindMap['processCollect']['inputs']]: UiFlowNodeInput.Opts<NodeControlSocketKindMap['processCollect']['inputs'][k]>;
        } = {
            solve: { socket: 'event', label: 'Solve Trigger' },
            collect: { socket: 'event', label: 'Collect Trigger' },
        };
        const dataInputs: {
            [k in keyof NodeDataSocketKindMap['processCollect']['inputs']]: UiFlowNodeInput.Opts<NodeDataSocketKindMap['processCollect']['inputs'][k]>;
        } = {
            materialId: { socket: 'string', label: 'Material ID' },
        };
        for (const de of nodeCfg.controlValues.dataElements.filter(de => de.collectionKind === ProcessDataElementCollectionKindEnum.Collect)) {
            if (de.type === ProcessDataElementKindEnum.String || de.type === ProcessDataElementKindEnum.Material || de.type === ProcessDataElementKindEnum.MaterialModel || de.type === ProcessDataElementKindEnum.MaterialClass) {
                // FUTURE: if we add more types like uuid or even more specific material uuid then we can handle that here
                dataInputs[de.id] = { socket: 'string', label: de.name };
            } else if (de.type === ProcessDataElementKindEnum.Number) {
                dataInputs[de.id] = { socket: 'number', label: de.name };
            } else if (de.type === ProcessDataElementKindEnum.Pass) {
                dataInputs[de.id] = { socket: 'boolean', label: de.name };
            }
        }
        return {
            ...controlInputs,
            ...dataInputs,
        } as ReturnType<UiNodeSpec<'processCollect'>['inputs']>;
    },
    outputs: nodeCfg => {
        const controlOutputs: {
            [k in keyof NodeControlSocketKindMap['processCollect']['outputs']]: UiFlowNodeOutput.Opts<NodeControlSocketKindMap['processCollect']['outputs'][k]>;
        } = {
            next: { socket: 'event', label: 'Next' },
        };
        const dataOutputs: {
            [k in keyof NodeDataSocketKindMap['processCollect']['outputs']]: UiFlowNodeOutput.Opts<NodeDataSocketKindMap['processCollect']['outputs'][k]>;
        } = {};

        if (nodeCfg.controlValues.useStandardizedOutputs.status) {
            dataOutputs.status = deletableOutputHelper({
                nodeCfg,
                label: 'Status',
                value: 'status',
                outputType: 'string',
            });
        }
        if (nodeCfg.controlValues.useStandardizedOutputs.specId) {
            dataOutputs.specId = deletableOutputHelper({
                nodeCfg,
                value: 'specId',
                label: 'Spec ID',
                outputType: 'string',
            });
        }
        if (nodeCfg.controlValues.useStandardizedOutputs.badCount) {
            dataOutputs.badCount = deletableOutputHelper({
                nodeCfg,
                value: 'badCount',
                label: 'Bad Count',
                outputType: 'number',
            });
        }
        if (nodeCfg.controlValues.useStandardizedOutputs.specName) {
            dataOutputs.specName = deletableOutputHelper({
                nodeCfg,
                value: 'specName',
                label: 'Specification Name',
                outputType: 'string',
            });
        }
        if (nodeCfg.controlValues.useStandardizedOutputs.goodCount) {
            dataOutputs.goodCount = deletableOutputHelper({
                nodeCfg,
                value: 'goodCount',
                label: 'Good Count',
                outputType: 'number',
            });
        }
        if (nodeCfg.controlValues.useStandardizedOutputs.requiredCount) {
            dataOutputs.requiredCount = deletableOutputHelper({
                nodeCfg,
                outputType: 'number',
                value: 'requiredCount',
                label: 'Required Count',
            });
        }
        if (nodeCfg.controlValues.useStandardizedOutputs.validationError) {
            dataOutputs.validationError = deletableOutputHelper({
                nodeCfg,
                outputType: 'string',
                label: 'Validation Error',
                value: 'validationError',
            });
        }

        for (const de of nodeCfg.controlValues.dataElements.filter(de => de.collectionKind === ProcessDataElementCollectionKindEnum.SpecParam)) {
            if (de.type === ProcessDataElementKindEnum.Material) {
                // material cannot be a spec param
            } else if (de.type === ProcessDataElementKindEnum.String) {
                dataOutputs[`de_${de.id}`] = { socket: 'string', label: de.name };
            } else if (de.type === ProcessDataElementKindEnum.Number) {
                dataOutputs[`de_${de.id}`] = { socket: 'number', label: de.name };
            } else if (de.type === ProcessDataElementKindEnum.Pass) {
                dataOutputs[`de_${de.id}`] = { socket: 'boolean', label: de.name };
            }
        }
        return {
            ...controlOutputs,
            ...dataOutputs,
        } as ReturnType<UiNodeSpec<'processCollect'>['outputs']>;
    },
    validator: node => {
        const errors: Record<string, string> = {};

        if (!node.controlValues.processId) {
            errors.processId = 'Process is required';
        }

        if ('deployParamKind' in node.controlValues.location) {
            if (!node.controlValues.location.deployParamId) {
                errors.location = 'Location deployment parameter is required';
            }
        } else if (!node.controlValues.location.id) {
            errors.location = 'Location is required';
        }

        return Object.keys(errors).length === 0 ? true : errors;
    },
};
